/** Recipes describe a client setup, never an additional authentication boundary. */
export const PROGRAMS = [
  { id: 'superset', name: 'Superset', descriptionKey: 'connectionGuide.programs.superset', kind: 'sql', accessType: 'bi' },
  { id: 'dbeaver', name: 'DBeaver', descriptionKey: 'connectionGuide.programs.dbeaver', kind: 'sql', accessType: 'sql_ide' },
  { id: 'claude-desktop', nameKey: 'connectionGuide.names.claudeConfig', descriptionKey: 'connectionGuide.programs.claudeDesktop', kind: 'key', channels: ['mcp'] },
  { id: 'claude-connectors', nameKey: 'connectionGuide.names.claudeConnectors', descriptionKey: 'connectionGuide.programs.claudeConnectors', kind: 'key', channels: ['mcp'] },
  // ChatGPT requires OAuth; the current MCP service accepts workspace API keys.
  { id: 'chatgpt', name: 'ChatGPT', kind: 'key', channels: ['mcp'], requestOnly: true },
  ...['superset', 'powerbi', 'tableau', 'metabase'].map((client) => ({
    id: `cube-sql-${client}`, kind: 'cube-sql', requestOnly: true,
    name: { superset: 'Apache Superset', powerbi: 'Power BI', tableau: 'Tableau', metabase: 'Metabase' }[client],
  })),
  { id: 'cube-rest', name: 'Cube REST API', descriptionKey: 'connectionGuide.programs.cubeRest', kind: 'key', channels: ['cube'], developer: true },
  { id: 'sql-rest', name: 'Trino HTTP API', descriptionKey: 'connectionGuide.programs.sqlRest', kind: 'sql', accessType: 'rest_api', developer: true },
];

/** Pick a familiar category first; application recipes stay optional. */
export const CONNECTION_OPTIONS = [
  // Cube SQL speaks PostgreSQL. Do not issue a Trino password for this route.
  { id: 'bi-tool', nameKey: 'connectionGuide.categories.bi.title', descriptionKey: 'connectionGuide.categories.bi.examples', kind: 'cube-sql', requestOnly: true, guideId: 'cube-sql-generic' },
  { id: 'sql-client', nameKey: 'connectionGuide.categories.sql.title', descriptionKey: 'connectionGuide.categories.sql.examples', kind: 'sql', accessType: 'sql_ide', guideId: 'sql-generic' },
  { id: 'ai-assistant', nameKey: 'connectionGuide.categories.ai.title', descriptionKey: 'connectionGuide.categories.ai.examples', kind: 'key', channels: ['mcp'], guideId: 'mcp-generic' },
  { id: 'trino-bi', nameKey: 'connectionGuide.names.trinoBi', descriptionKey: 'connectionGuide.programs.trinoBi', kind: 'sql', accessType: 'bi', guideId: 'sql-generic', developer: true },
  ...PROGRAMS.filter((program) => program.developer),
];

const GENERIC_PROGRAMS = [
  { id: 'cube-sql-generic', nameKey: 'connectionGuide.generic.cubeSql', kind: 'cube-sql', requestOnly: true },
  { id: 'sql-generic', nameKey: 'connectionGuide.generic.sql', kind: 'sql' },
  { id: 'mcp-generic', nameKey: 'connectionGuide.generic.mcp', kind: 'key', channels: ['mcp'] },
  { id: 'cube-generic', nameKey: 'connectionGuide.generic.cube', kind: 'key', channels: ['cube'] },
  { id: 'key-generic', nameKey: 'connectionGuide.generic.key', kind: 'key' },
];

export const getProgram = (id) => [...CONNECTION_OPTIONS, ...PROGRAMS, ...GENERIC_PROGRAMS].find((program) => program.id === id);

/** The server stores access categories, not client identities. Names are user input. */
export function defaultProgramForCredential(kind, credential) {
  if (kind === 'cube-sql') return 'cube-sql-generic';
  if (kind === 'sql') return 'sql-generic';
  const channels = credential?.channels || [];
  if (channels.length === 1 && channels[0] === 'mcp') return 'mcp-generic';
  if (channels.length === 1 && channels[0] === 'cube') return 'cube-generic';
  return 'key-generic';
}

export function getProgramsForCredential(kind, credential) {
  return PROGRAMS.filter((program) => program.kind === kind && (
    !program.channels || program.channels.every((channel) => (credential?.channels || []).includes(channel))
  ));
}

/** Build only from server-supplied connection fields; secrets never enter persisted data. */
export function buildSqlConnectionUri(info, secret) {
  if (!info?.host || !info?.port || !info?.username || !info?.catalog || !info?.schema) return '';
  const host = info.host.includes(':') && !info.host.startsWith('[') ? `[${info.host}]` : info.host;
  const password = typeof secret === 'string' && secret.length ? encodeURIComponent(secret) : '{password}';
  return `trino://${encodeURIComponent(info.username)}:${password}@${host}:${info.port}/${encodeURIComponent(info.catalog)}/${encodeURIComponent(info.schema)}`;
}

/** Header interpolation avoids the Windows npx argument-space issue documented by mcp-remote. */
export function buildClaudeDesktopConfig(endpoint, secret) {
  if (!endpoint) return null;
  return {
    mcpServers: {
      'evolve-analytics': {
        command: 'npx',
        // eslint-disable-next-line no-template-curly-in-string -- mcp-remote expands this environment reference.
        args: ['-y', 'mcp-remote', endpoint, '--header', 'Authorization:${EVOLVE_AUTH_HEADER}'],
        env: { EVOLVE_AUTH_HEADER: `Bearer ${secret || 'YOUR_API_KEY'}` },
      },
    },
  };
}
